import React, { useCallback } from 'react'

import { useHistory } from 'react-router-dom'

import {
  Button,
  ButtonGroup,
  Flex,
  Header,
  IconButton,
  InlineAlert,
  MenuButton,
  Paper,
  Table,
} from '@driftt/dds'
import { faEye } from '@driftt/dds.icons/regular/faEye'
import { faLink } from '@driftt/dds.icons/regular/faLink'
import { faPenToSquare } from '@driftt/dds.icons/regular/faPenToSquare'
import { faTrash } from '@driftt/dds.icons/regular/faTrash'
import { CubeDataProvider } from '@driftt/dds.reports'
import { access } from '@driftt/traffic-control'

import ConferenceTypeIcon from 'components/ConferenceTypeIcon'
import DeleteMeetingTypeModal from 'components/DeleteMeetingTypeModal'
import { MeetingNameWithBadge } from 'components/MeetingNameWithBadge'
import { MeetingsBookedTable } from 'components/MeetingsBookedTable'
import UserListTooltip from 'components/UserListTooltip'
import useCalendarStatus from 'hooks/useCalendarStatus'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import useIsAdmin from 'hooks/useIsAdmin'
import useLocalStorage from 'hooks/useLocalStorage'
import useMeetingTypesList from 'hooks/useMeetingTypesList'
import useProfilePath from 'hooks/useProfilePath'
import { AgentAvailability, MeetingTypeWithId } from 'types/MeetingType'
import {
  trackMeetingCreated,
  trackMeetingLinkCopied,
  trackOrgMeetingCreated,
} from 'utils/tracking'

export default function Page() {
  const history = useHistory()
  const copyToClipboard = useCopyToClipboard()
  const { data } = useCalendarStatus()

  const [hasDismissedBanner, setHasDismissedBanner] = useLocalStorage(
    'settings-moved-banner',
    false
  )

  const profilesPath = useProfilePath()
  const userIsAdmin = useIsAdmin()

  const { meetingTypes, isLoading } = useMeetingTypesList()
  const [selectedMeeting, setSelectedMeeting] =
    React.useState<MeetingTypeWithId>()

  const getAgentIds = useCallback(
    (agentAvailabilities: AgentAvailability[]) =>
      agentAvailabilities.map((availability) => availability.agentId),
    []
  )

  const showMeetingsBookedTable = access.hasGates('measure_meetings')
  const hasOrgWideMeetingWriteAccess =
    userIsAdmin ||
    access.hasFeatures([{ featureName: 'org_meetings', accessLevel: 'WRITE' }])
  return (
    <>
      {data?.connected && !hasDismissedBanner && (
        <InlineAlert
          css={{
            marginBottom: '$4',
          }}
          title="Settings have moved"
          description="Availability and connected accounts now live in Settings > App Settings > Meetings."
          variant="info"
          dismissable
          onClose={() => setHasDismissedBanner(true)}
          actionText="Go to Settings"
          onActionClick={() => {
            window.open('/settings2/meetings', '_blank')
          }}
        />
      )}
      {!data?.connected && (
        <InlineAlert
          css={{
            marginBottom: '$4',
          }}
          title="Your calendar is not connected"
          description="You won't be able to book meetings until you connect your calendar"
          variant="error"
          actionText="Go to Settings"
          onActionClick={() => {
            window.open('/settings2/meetings', '_blank')
          }}
        />
      )}
      {showMeetingsBookedTable && (
        <CubeDataProvider>
          <MeetingsBookedTable />
        </CubeDataProvider>
      )}
      <Paper elevation={2}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          css={{ padding: '$3 $2 $2' }}
        >
          <Header variant="h2">Meeting types</Header>
          {hasOrgWideMeetingWriteAccess ? (
            <MenuButton
              buttonText="Create meeting type"
              options={[
                {
                  name: 'Create personal meeting type',
                  action: () => {
                    trackMeetingCreated()
                    history.push('/new', {
                      meetingId: '',
                      isNewMeeting: true,
                      isOrgMeeting: false,
                      isDefault: false,
                      meetingName: 'New meeting type',
                    })
                  },
                },
                {
                  name: 'Create org-wide meeting type',
                  action: () => {
                    trackOrgMeetingCreated()
                    history.push('/new', {
                      meetingId: '',
                      isNewMeeting: true,
                      isOrgMeeting: true,
                      isDefault: false,
                      meetingName: ' New meeting type',
                    })
                  },
                },
              ]}
            />
          ) : (
            <Button
              onClick={() =>
                history.push('/new', {
                  meetingId: '',
                  isNewMeeting: true,
                  isOrgMeeting: false,
                  isDefault: false,
                  meetingName: ' New meeting type',
                })
              }
            >
              Create meeting type
            </Button>
          )}
        </Flex>
        <Flex direction="column" fullWidth gap={2}>
          <Table
            isLoading={isLoading}
            loadingRows={5}
            disableBoxShadow
            columns={[
              {
                header: 'Name',
                value: 'id',
                cell: (meeting) => (
                  <div>
                    <Flex alignItems="center" fullWidth>
                      <MeetingNameWithBadge meeting={meeting} />
                    </Flex>
                  </div>
                ),
              },
              {
                value: 'duration',
                header: 'Duration',
                cell: ({ duration }) => `${duration} minutes`,
              },
              {
                value: 'agentAvailabilities',
                header: 'Event type',
                cell: ({ agentAvailabilities, conferenceType }) => (
                  <Flex alignItems="center">
                    {agentAvailabilities ? (
                      <UserListTooltip
                        userIds={getAgentIds(agentAvailabilities)}
                      >
                        Group
                      </UserListTooltip>
                    ) : (
                      'One-on-one'
                    )}{' '}
                    <ConferenceTypeIcon type={conferenceType} />
                  </Flex>
                ),
              },
              {
                id: 'actions',
                align: 'right',
                render: (meeting) => {
                  const isReadOnly =
                    !!meeting.orgMeeting && !hasOrgWideMeetingWriteAccess

                  return (
                    <ButtonGroup>
                      <IconButton
                        icon={isReadOnly ? faEye : faPenToSquare}
                        title={isReadOnly ? 'View' : 'Edit'}
                        onClick={() =>
                          history.push(`/edit/${meeting.id}`, {
                            meetingId: meeting.id,
                            isNewMeeting: false,
                            isOrgMeeting: meeting.orgMeeting,
                            isDefault: meeting.default,
                            meetingName: meeting.name,
                          })
                        }
                      />
                      <IconButton
                        icon={faLink}
                        title="Copy link"
                        onClick={() => {
                          trackMeetingLinkCopied()
                          copyToClipboard(
                            `${profilesPath}/meeting/${meeting.id}`
                          )
                        }}
                      />
                      {!meeting.default && !isReadOnly && (
                        <IconButton
                          icon={faTrash}
                          title="Delete"
                          onClick={() => setSelectedMeeting(meeting)}
                        />
                      )}
                    </ButtonGroup>
                  )
                },
              },
            ]}
            rows={meetingTypes}
            pagination={{
              rowTotal: meetingTypes.length,
              defaultPagination: {
                pageIndex: 0,
                pageSize: 25,
              },
              enableClientSidePagination: true,
            }}
          />
        </Flex>
      </Paper>
      <DeleteMeetingTypeModal
        meeting={selectedMeeting}
        onClose={() => setSelectedMeeting(undefined)}
      />
    </>
  )
}

Page.Header = function PageHeader() {
  return <Header variant="h1">Meetings</Header>
}
